/**
 * Create gift matching pool page
 *
 * Builds a pool from an existing gift match: pick the source campaign and gift match, check the
 * read-only summary of what the pool will copy, add the campaigns to share it with, and save once.
 * The gift match itself is changed on its campaign. Opened with `?campaignId=&giftMatchingId=`
 * from a campaign's gift matching section.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Form, Message, Segment } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { systemSettingsRoutes } from '../constants';
import { createGiftMatchingPool, getPoolCampaignSummary } from './api';
import { i18nKeys, nameMaxLength } from './constants';
import {
  errorMessageKeys,
  hasPageConditions,
  isCurrencyMismatch,
  isMultiOrganisation
} from './helpers';
import { CampaignGiftMatch, CampaignSummary } from './types';
import CampaignPicker from './components/CampaignPicker';
import GiftMatchSummary from './components/GiftMatchSummary';
import MultiOrganisationWarning from './components/MultiOrganisationWarning';
import TargetCampaignsList from './components/TargetCampaignsList';
import Styles from './GiftMatchingPools.module.css';

const ineligibleReason = (giftMatch: CampaignGiftMatch): string | null => {
  if (giftMatch.isPoolEntry) {
    return i18nKeys.CREATE.SOURCE_POOLED;
  }
  if (hasPageConditions(giftMatch.conditions)) {
    return i18nKeys.CREATE.SOURCE_PAGE_CONDITIONS;
  }
  if ((giftMatch.name?.trim().length ?? 0) > nameMaxLength) {
    return i18nKeys.CREATE.SOURCE_NAME_TOO_LONG;
  }
  return null;
};

const GiftMatchingPoolCreate: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [source, setSource] = useState<CampaignSummary | null>(null);
  const [sourceGiftMatchId, setSourceGiftMatchId] = useState<number | null>(
    null
  );
  const [sourceErrorKey, setSourceErrorKey] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [targets, setTargets] = useState<CampaignSummary[]>([]);
  const [targetErrorKey, setTargetErrorKey] = useState<string | null>(null);
  const [submitErrorKeys, setSubmitErrorKeys] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Shows the campaign and keeps the chosen gift match when it can still be pooled.
  const applySource = (
    summary: CampaignSummary,
    giftMatchingId?: number | null
  ) => {
    setSource(summary);
    const chosen = summary.giftMatching.find((g) => g.id === giftMatchingId);
    const reason = chosen ? ineligibleReason(chosen) : null;
    setSourceErrorKey(reason);
    setSourceGiftMatchId(chosen && !reason ? chosen.id : null);
  };

  const loadSource = async (campaignId: number, giftMatchingId?: number) => {
    setSourceErrorKey(null);
    setSourceGiftMatchId(null);
    try {
      const summary = await getPoolCampaignSummary(campaignId);
      setTargets((current) =>
        current.filter((t) => t.campaignId !== campaignId)
      );
      applySource(summary, giftMatchingId);
    } catch {
      setSource(null);
      setSourceErrorKey(i18nKeys.ERRORS.SUMMARY_FAILED);
    }
  };

  const refreshSource = async () => {
    if (!source) {
      return;
    }
    setRefreshing(true);
    try {
      applySource(
        await getPoolCampaignSummary(source.campaignId),
        sourceGiftMatchId
      );
    } catch {
      setSourceErrorKey(i18nKeys.ERRORS.SUMMARY_FAILED);
    } finally {
      setRefreshing(false);
    }
  };

  // Runs once, for the query string the page was opened with.
  useEffect(() => {
    const campaignId = Number(searchParams.get('campaignId'));
    const giftMatchingId = Number(searchParams.get('giftMatchingId'));
    if (Number.isInteger(campaignId) && campaignId > 0) {
      loadSource(campaignId, giftMatchingId || undefined);
    }
  }, []);

  const addTarget = async (campaignId: number) => {
    setTargetErrorKey(null);
    if (source && campaignId === source.campaignId) {
      setTargetErrorKey(i18nKeys.ERRORS.CAMPAIGN_ALREADY_LINKED);
      return;
    }
    if (targets.some((t) => t.campaignId === campaignId)) {
      setTargetErrorKey(i18nKeys.WARNINGS.DUPLICATE);
      return;
    }
    try {
      const summary = await getPoolCampaignSummary(campaignId);
      setTargets((current) =>
        current.some((t) => t.campaignId === summary.campaignId)
          ? current
          : [...current, summary]
      );
    } catch {
      setTargetErrorKey(i18nKeys.ERRORS.SUMMARY_FAILED);
    }
  };

  const organisationIds = useMemo(
    () => [
      ...(source ? [source.organizationId] : []),
      ...targets.map((t) => t.organizationId)
    ],
    [source, targets]
  );
  const hasCurrencyMismatch = targets.some((t) =>
    isCurrencyMismatch(t.currency, source?.currency)
  );
  const sourceGiftMatch =
    source?.giftMatching.find((g) => g.id === sourceGiftMatchId) ?? null;

  const onSubmit = async () => {
    const keys: string[] = [];
    if (!sourceGiftMatchId) {
      keys.push(i18nKeys.ERRORS.SOURCE_REQUIRED);
    }
    if (targets.length === 0) {
      keys.push(i18nKeys.ERRORS.TARGETS_REQUIRED);
    }
    if (hasCurrencyMismatch) {
      keys.push(i18nKeys.ERRORS.CURRENCY);
    }
    setSubmitErrorKeys(keys);
    if (keys.length > 0 || !sourceGiftMatchId) {
      return;
    }
    setSubmitting(true);
    try {
      const pool = await createGiftMatchingPool({
        campaignIds: targets.map((t) => t.campaignId),
        sourceGiftMatchingId: sourceGiftMatchId
      });
      navigate(`${systemSettingsRoutes.GIFT_MATCHING_POOLS_EDIT}/${pool.id}`, {
        state: { created: true }
      });
    } catch (error) {
      setSubmitErrorKeys(
        errorMessageKeys(error, i18nKeys.ERRORS.CREATE_FAILED)
      );
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className={Styles.header}>
        <h2>
          <Translate value={i18nKeys.CREATE.TITLE} />
        </h2>
        <Button
          basic
          type="button"
          onClick={() => navigate(systemSettingsRoutes.GIFT_MATCHING_POOLS)}
        >
          <Translate value={i18nKeys.BACK} />
        </Button>
      </div>

      <Form onSubmit={onSubmit} noValidate>
        <Segment className={Styles.section}>
          <h3>
            <Translate value={i18nKeys.CREATE.SOURCE_TITLE} />
          </h3>
          <p className="field-description">
            <Translate value={i18nKeys.CREATE.SOURCE_HELP} />
          </p>
          {source ? (
            <p>
              <strong>
                {source.campaignName} ({source.campaignId})
              </strong>
              {source.organizationName && ` · ${source.organizationName}`}
              <Button
                basic
                size="tiny"
                type="button"
                style={{ marginLeft: '1rem' }}
                onClick={() => {
                  setSource(null);
                  setSourceGiftMatchId(null);
                  setSourceErrorKey(null);
                }}
              >
                <Translate value={i18nKeys.CREATE.SOURCE_CHANGE} />
              </Button>
            </p>
          ) : (
            <CampaignPicker
              idPrefix="pool-source"
              label={I18n.t(i18nKeys.CREATE.SOURCE_CAMPAIGN)}
              onPick={(campaignId) => loadSource(campaignId)}
            />
          )}
          {sourceErrorKey && (
            <Message negative role="alert" content={I18n.t(sourceErrorKey)} />
          )}
          {source && source.giftMatching.length === 0 && (
            <Message info content={I18n.t(i18nKeys.CREATE.NO_GIFT_MATCHES)} />
          )}
          {source && source.giftMatching.length > 0 && (
            <fieldset className={Styles.fieldset}>
              <legend className={Styles.legend}>
                <Translate value={i18nKeys.CREATE.SOURCE_GIFT_MATCH} />
              </legend>
              {source.giftMatching.map((giftMatch) => {
                const reason = ineligibleReason(giftMatch);
                return (
                  <Form.Field
                    key={giftMatch.id}
                    className={Styles.giftMatchOption}
                  >
                    <Checkbox
                      radio
                      id={`pool-source-gift-match-${giftMatch.id}`}
                      name="pool-source-gift-match"
                      label={`${giftMatch.name} (x${giftMatch.multiplier})`}
                      checked={sourceGiftMatchId === giftMatch.id}
                      disabled={!!reason}
                      onChange={() => {
                        if (!reason) {
                          setSourceGiftMatchId(giftMatch.id);
                        }
                      }}
                    />
                    {reason && (
                      <span className={Styles.flag}>{I18n.t(reason)}</span>
                    )}
                  </Form.Field>
                );
              })}
            </fieldset>
          )}
          {source && sourceGiftMatch && (
            <GiftMatchSummary
              campaignId={source.campaignId}
              currency={source.currency}
              giftMatch={sourceGiftMatch}
              refreshing={refreshing}
              onRefresh={refreshSource}
            />
          )}
        </Segment>

        <Segment className={Styles.section}>
          <h3>
            <Translate value={i18nKeys.CREATE.TARGETS_TITLE} />
          </h3>
          {sourceGiftMatch ? (
            <>
              <p className="field-description">
                <Translate value={i18nKeys.CREATE.TARGETS_HELP} />
              </p>
              <CampaignPicker
                idPrefix="pool-targets"
                label={I18n.t(i18nKeys.PICKER.SEARCH)}
                organizationId={source?.organizationId}
                organizationName={source?.organizationName}
                onPick={addTarget}
              />
              {targetErrorKey && (
                <Message
                  negative
                  role="alert"
                  content={I18n.t(targetErrorKey)}
                />
              )}
              {isMultiOrganisation(organisationIds) && (
                <MultiOrganisationWarning />
              )}
              <TargetCampaignsList
                campaigns={targets}
                poolCurrency={source?.currency ?? null}
                onRemove={(campaignId) =>
                  setTargets((current) =>
                    current.filter((t) => t.campaignId !== campaignId)
                  )
                }
              />
            </>
          ) : (
            <p className="field-description">
              <Translate value={i18nKeys.CREATE.CHOOSE_SOURCE_FIRST} />
            </p>
          )}
        </Segment>

        {submitErrorKeys.length > 0 && (
          <Message negative role="alert">
            {submitErrorKeys.map((key) => (
              <p key={key}>{I18n.t(key)}</p>
            ))}
          </Message>
        )}
        {sourceGiftMatch && (
          <div className={Styles.actions}>
            <Button
              primary
              type="submit"
              loading={submitting}
              disabled={submitting}
            >
              <Translate value={i18nKeys.CREATE.SUBMIT} />
            </Button>
          </div>
        )}
      </Form>
    </div>
  );
};

export default GiftMatchingPoolCreate;
